import React, { useMemo, useContext } from 'react';
import {
  Button, ButtonVariant
} from '@patternfly/react-core';
import { DateTime } from 'luxon';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../localization/useTranslation';
import AlarmIconMulti from './Icons/AlarmIconMulti';
import { useAlarmPushService } from '../../services/AlarmApi/AlarmApi';

import useAPI from '../../services/useAPI';
import { useMutation } from '@tanstack/react-query';
import createMutationRequest from 'utils/createMutationRequest';
import { useQuery } from '@tanstack/react-query';
import createQueryRequest from 'services/createQueryRequest';
import { assetKeys } from 'components/Admin/Assets/queries';

import SimpleTable, { SortDirection } from '../Common/SimpleTable/SimpleTable';
import { TableText } from '@patternfly/react-table';
import NodeContext from '../../services/Context/NodeContext';
import { walkParentPath } from 'utils/nodes';
import { isoToRelativeOrAbsolute } from 'utils/time';
import { Icons } from 'components/Common';

const findNode = (startNode, id) => {
  if (startNode?.id === id) {
    return startNode;
  }

  return _.find(startNode?.assetDescendants, { id });
};
const nodeId = (node) => node?.id ?? node?._key;

const createNodePath = (nodePath) => {
  return `/view/tree/${_.initial(nodePath).map(nodeId).join('/') + '/asset/' + nodeId(_.last(nodePath))}`;
};

const DefaultSortBy = {
  path: 'name',
  direction: SortDirection.DESC
};

const ActiveAlarmList = () => {
  const nodeContext = useContext(NodeContext);
  const T = useTranslation();
  const api = useAPI();

  const assetsQuery = useQuery({
    queryKey: assetKeys.assets(),
    queryFn: createQueryRequest(api.core.getAssets)
  });

  const assets = useMemo(() => {
    return assetsQuery.data;
  }, [assetsQuery.data]);

  const filteredAssetIds = useMemo(() => {
    const assetsDescendants = !_.isEmpty(nodeContext?.node?.assetDescendants) ? nodeContext?.node?.assetDescendants : [nodeContext?.node] ?? [];
    const filteredAssets = _.filter(assets, (asset) => {
      return assetsDescendants.some((descendant) => {
        return (descendant?.id === asset?.id);
      });
    });
    return _.uniq(_.map(filteredAssets, 'id'));
  }, [assets, nodeContext?.node]);

  const [liveAlarms, isLoadingAlarms] = useAlarmPushService(
    useMemo(() => ({ assetIds: filteredAssetIds }), [filteredAssetIds]));

  const alarmAckQuery = useMutation({
    mutationFn: createMutationRequest(api.core.acknowledgeAlarmStatus),
    onSuccess: _.noop,
    onError: _.noop
  });

  const isLoading = assetsQuery.isLoading || isLoadingAlarms;

  const alarms = useMemo(() => {
    if (_.isEmpty(assets) || !nodeContext?.node) {
      return [];
    }
    const alarmArray = _.map(liveAlarms, assetAlarm => {
      const asset = _.first(_.filter(assets, ['id', assetAlarm.entityId]));
      return {
        ...assetAlarm,
        created: DateTime.fromISO(assetAlarm.created),
        timestamp: DateTime.fromISO(assetAlarm.timestamp),
        status: {
          status: assetAlarm.status,
          isAcknowledged: assetAlarm.isAcknowledged,
          acknowledgedBy: assetAlarm.acknowledgedBy,
          acknowledgedTime: assetAlarm.acknowledgedTime
        },
        asset: {
          id: asset.id,
          displayName: asset.displayName
        },
        sillyAlarmCopy: {
          id: assetAlarm.id,
          isAcknowledged: assetAlarm.isAcknowledged,
          acknowledgedBy: assetAlarm.acknowledgedBy,
          acknowledgedTime: assetAlarm.acknowledgedTime
        }
      };
    });
    if (!_.isEmpty(nodeContext?.node?.assetDescendants)) {
      const desc = alarmArray.filter((alarm) => {
        return nodeContext?.node.assetDescendants.some((asset) => {
          return (asset.id === alarm.entityId);
        });
      });
      const item = alarmArray.filter((alarm) => {
        return alarm.entityId === nodeContext?.node._key;
      });
      return [...item, ...desc];
    }
    return alarmArray;
  }, [assets, nodeContext?.node, liveAlarms]);

  const columns = useMemo(() => {
    return [
      {
        label: T.common.status,
        path: 'status',
        searchItem: true,
        isSortable: true,
        render: val => <AlarmIconMulti data={val} tooltipDirection="right" />
      },
      {
        label: T.common.alarm,
        path: '.',
        isSortable: true,
        render: alarm => {
          return (
            <TableText tooltip="hepp" variant="span">
              { alarm.name }
            </TableText>
          );
        }
      },
      {
        label: T.common.asset,
        path: '.',
        isSortable: true,
        render: alarm => {
          const asset = findNode(nodeContext?.node, alarm.asset.id);
          if (asset != null) {
            return <Link to={createNodePath(walkParentPath(asset))}>{ alarm.asset.displayName }</Link>;
          }
          return '';
        }
      },
      {
        label: T.alarm.firstTriggered,
        path: 'created',
        isSortable: true,
        render: created => isoToRelativeOrAbsolute(created)
      },
      {
        label: T.alarm.lastTriggered,
        path: 'timestamp',
        isSortable: true,
        render: timestamp => isoToRelativeOrAbsolute(timestamp)
      },
      {
        label: T.alarm.acknowledge,
        path: 'sillyAlarmCopy',
        isSortable: true,
        render: (alarm) => {
          if (alarm.isAcknowledged === true) {
            return null;
          }
          return (
            <>
              <Button
                isLoading={alarmAckQuery.isLoading}
                onClick={() => alarmAckQuery.mutate(alarm.id)}
                variant={ButtonVariant.secondary}
                icon={<Icons.Checkmark />} />
            </>
          );

        }
      }
    ];
  }, [T.alarm.acknowledge, T.alarm.firstTriggered, T.alarm.lastTriggered, T.common.alarm, T.common.asset, T.common.status, alarmAckQuery, nodeContext?.node]);

  return (
    <>
      <SimpleTable
        isLoading={isLoading}
        data={alarms}
        columns={columns}
        sortBy={DefaultSortBy}
        compact
        emptyListText={T.alarm.noAlarms}
      />
    </>
  );
};

export default ActiveAlarmList;
